import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import {
  IsOptional,
  IsString,
  IsInt,
  Min,
  Max,
  IsEnum,
} from 'class-validator'
import { Type } from 'class-transformer'

/**
 * Ordre de tri (ascendant ou descendant)
 */
export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

/**
 * Configuration par défaut de la pagination
 */
export const PAGINATION_DEFAULTS = {
  LIMIT: 25,
  MAX_LIMIT: 100,
  PAGE: 1,
} as const

/**
 * DTO pour la pagination offset-based
 */
export class OffsetPaginationQueryDto {
  @ApiPropertyOptional({
    type: Number,
    description: 'Numéro de page (1 = première page)',
    default: PAGINATION_DEFAULTS.PAGE,
    minimum: 1,
    example: 1,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number = PAGINATION_DEFAULTS.PAGE

  @ApiPropertyOptional({
    type: Number,
    description: "Nombre d'éléments par page",
    default: PAGINATION_DEFAULTS.LIMIT,
    minimum: 1,
    maximum: PAGINATION_DEFAULTS.MAX_LIMIT,
    example: 25,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(PAGINATION_DEFAULTS.MAX_LIMIT)
  limit?: number = PAGINATION_DEFAULTS.LIMIT

  @ApiPropertyOptional({
    type: 'string',
    description: 'Champ de tri',
    example: 'createdAt',
  })
  @IsOptional()
  @IsString()
  sort?: string

  @ApiPropertyOptional({
    type: 'string',
    description: 'Ordre de tri',
    enum: SortOrder,
    default: SortOrder.DESC,
    example: 'desc',
  })
  @IsOptional()
  @IsEnum(SortOrder)
  order?: SortOrder = SortOrder.DESC

  @ApiPropertyOptional({
    type: 'string',
    description: 'Recherche textuelle',
  })
  @IsOptional()
  @IsString()
  q?: string
}

/**
 * Métadonnées de pagination pour offset-based
 */
export class OffsetPageInfo {
  @ApiProperty({ description: 'Numéro de page (1 = première page)', example: 1 })
  page!: number

  @ApiProperty({ description: "Nombre d'éléments par page", example: 25 })
  limit!: number

  @ApiProperty({ description: "Nombre total d'éléments", example: 150 })
  totalCount!: number

  @ApiProperty({ description: "Nombre total de pages", example: 6 })
  totalPages!: number

  @ApiProperty({ description: 'Y a-t-il une page suivante ?', example: true })
  hasNextPage!: boolean

  @ApiProperty({ description: 'Y a-t-il une page précédente ?', example: false })
  hasPreviousPage!: boolean

  @ApiProperty({ description: 'Champ de tri utilisé', example: 'createdAt' })
  sort!: string

  @ApiProperty({ description: 'Ordre de tri (asc/desc)', example: 'desc' })
  order!: string
}

/**
 * Réponse de pagination offset-based
 */
export class OffsetPaginationResponseDto<T> {
  @ApiProperty({ isArray: true }) // On dit juste que c'est un tableau
    data: T[];


  @ApiProperty({
    description: 'Métadonnées de pagination',
    type: 'object',
    additionalProperties: false,
    properties: {
      page: { type: 'number', example: 1, description: 'Numéro de page' },
      limit: { type: 'number', example: 25, description: "Nombre d'éléments par page" },
      totalCount: { type: 'number', example: 150, description: "Nombre total d'éléments" },
      totalPages: { type: 'number', example: 6, description: "Nombre total de pages" },
      hasNextPage: { type: 'boolean', example: true, description: 'Page suivante disponible' },
      hasPreviousPage: { type: 'boolean', example: false, description: 'Page précédente disponible' },
      sort: { type: 'string', example: 'createdAt', description: 'Champ de tri' },
      order: { type: 'string', example: 'desc', description: 'Ordre de tri' },
    },
    required: ['page', 'limit', 'totalCount', 'totalPages', 'hasNextPage', 'hasPreviousPage', 'sort', 'order'],
  })
  pageInfo: OffsetPageInfo

  constructor(data: T[], pageInfo: OffsetPageInfo) {
    this.data = data
    this.pageInfo = pageInfo
  }
}
